import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { useUi } from '../../context/UiContext';
import { PricingMarketSwitch } from '../public/PricingMarketSwitch';
import { useAuth } from '../../auth/AuthContext';
import { customerSignInPath } from '../../auth/experience';
import { BrandLogo } from '../brand/BrandWordmark';
import type { Language, Theme } from '../../types';
import {
  Search,
  Sparkles,
  Sun,
  Moon,
  Laptop,
  Globe,
  Menu,
  X,
  LogOut,
  LogIn,
  Package,
  ShieldCheck,
  User,
} from 'lucide-react';

const iconBtn =
  'p-2 rounded-md border border-brand-500/25 bg-brand-500/10 text-brand-600 dark:text-brand-400 hover:border-brand-500/50 hover:bg-brand-500/15 active:bg-brand-500/20 transition-colors duration-200';

type Experience = 'public' | 'customer' | 'admin';

interface HeaderProps {
  experience?: Experience;
}

export const Header: React.FC<HeaderProps> = ({ experience = 'public' }) => {
  const { language, setLanguage, t } = useLanguage();
  const { theme, setTheme } = useTheme();
  const { setIsCommandPaletteOpen, setIsAiCopilotOpen } = useUi();
  const { user, logout, isAdmin, isAuthenticated } = useAuth();
  const [isLangMenuOpen, setIsLangMenuOpen] = React.useState(false);
  const [isThemeMenuOpen, setIsThemeMenuOpen] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const headerRef = React.useRef<HTMLElement>(null);
  const langMenuRef = React.useRef<HTMLDivElement>(null);
  const themeMenuRef = React.useRef<HTMLDivElement>(null);

  const isAdminShell = experience === 'admin';
  const isCustomerShell = experience === 'customer';
  const accountPath = isAdminShell ? '/admin/account' : '/customer/profile';
  const portalPath = isAdminShell ? '/admin' : '/customer';
  const signInPath = isAdminShell ? '/admin/login' : customerSignInPath();

  React.useEffect(() => {
    if (!isLangMenuOpen && !isThemeMenuOpen) return;
    const onDocPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (langMenuRef.current?.contains(target) || themeMenuRef.current?.contains(target)) {
        return;
      }
      setIsLangMenuOpen(false);
      setIsThemeMenuOpen(false);
    };
    document.addEventListener('pointerdown', onDocPointerDown);
    return () => document.removeEventListener('pointerdown', onDocPointerDown);
  }, [isLangMenuOpen, isThemeMenuOpen]);

  const navLink =
    'text-xs font-semibold text-fog hover:text-ink dark:hover:text-paper transition-colors duration-200';

  const publicLinks: [string, string][] = [
    ['/applications', 'nav.products'],
    ['/pricing', 'nav.contactSales'],
    ['/docs', 'nav.docs'],
    ['/status', 'nav.status'],
  ];

  const customerLinks: [string, string][] = [
    ['/customer', 'nav.dashboard'],
    ['/customer/products', 'nav.myProducts'],
    ['/customer/subscriptions', 'nav.subscriptions'],
    ['/applications', 'nav.applications'],
  ];

  const desktopNav = isAdminShell
    ? null
    : isCustomerShell
      ? customerLinks
      : publicLinks;

  return (
    <header
      ref={headerRef}
      className="sticky top-0 z-40 w-full border-b border-line/80 dark:border-line-dark bg-paper/95 dark:bg-ink/95 backdrop-blur-md shadow-[0_1px_0_rgba(10,10,11,0.03)]"
    >
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:start-2 focus:z-50 focus:px-3 focus:py-2 focus:bg-brand-500 focus:text-white focus:rounded-md text-xs"
      >
        {t('a11y.skipToContent')}
      </a>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-4">
        <div className="flex items-center gap-8 min-w-0">
          <BrandLogo size="sm" to={isAdminShell ? '/admin' : '/'} />

          {isAdminShell ? (
            <p className="hidden sm:block text-[10px] font-semibold uppercase tracking-[0.16em] text-brand-500">
              {t('admin.title')}
            </p>
          ) : (
            <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
              {desktopNav?.map(([path, key]) => (
                <Link key={path} to={path} className={navLink}>
                  {t(key)}
                </Link>
              ))}
              {experience === 'public' && isAuthenticated && !isAdmin && (
                <Link to="/customer" className={navLink}>
                  {t('nav.dashboard')}
                </Link>
              )}
            </nav>
          )}
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {!isAdminShell && <PricingMarketSwitch compact />}
          {!isAdminShell && (
            <>
              <button
                type="button"
                onClick={() => setIsCommandPaletteOpen(true)}
                className="hidden sm:flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-brand-500/25 bg-brand-500/10 text-brand-600 dark:text-brand-400 hover:border-brand-500/50 hover:bg-brand-500/15 text-xs transition-colors duration-200"
                aria-label={t('search.placeholder')}
              >
                <Search className="w-3.5 h-3.5" />
                <kbd className="font-mono text-[10px] text-brand-700 dark:text-brand-300">⌘K</kbd>
              </button>
              {experience === 'public' && (
                <button
                  type="button"
                  onClick={() => setIsAiCopilotOpen(true)}
                  className={iconBtn}
                  aria-label={t('ai.title')}
                >
                  <Sparkles className="w-4 h-4" />
                </button>
              )}
            </>
          )}

          <div className="relative" ref={langMenuRef}>
            <button
              type="button"
              onClick={() => {
                setIsThemeMenuOpen(false);
                setIsLangMenuOpen((open) => !open);
              }}
              className={`${iconBtn} flex items-center gap-1 uppercase text-[10px] font-bold`}
              aria-label={t('nav.language')}
              aria-expanded={isLangMenuOpen}
              aria-haspopup="menu"
            >
              <Globe className="w-3.5 h-3.5" />
              {language}
            </button>
            {isLangMenuOpen && (
              <div
                role="menu"
                className="absolute end-0 mt-2 w-44 rounded-lg bg-white dark:bg-ink-elevated border border-line dark:border-line-dark p-1 z-[60] text-xs shadow-sm"
              >
                {(
                  [
                    ['en', 'English (LTR)'],
                    ['ar', 'العربية (RTL)'],
                    ['ckb', 'کوردی سۆرانی (RTL)'],
                  ] as [Language, string][]
                ).map(([code, label]) => (
                  <button
                    key={code}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setLanguage(code);
                      setIsLangMenuOpen(false);
                    }}
                    className={`w-full px-3 py-2 rounded-md text-start font-medium transition-colors ${
                      language === code
                        ? 'bg-brand-500/10 text-brand-600'
                        : 'hover:bg-paper dark:hover:bg-ink-soft'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative" ref={themeMenuRef}>
            <button
              type="button"
              onClick={() => {
                setIsLangMenuOpen(false);
                setIsThemeMenuOpen((open) => !open);
              }}
              className={iconBtn}
              aria-label={t('nav.theme')}
              aria-expanded={isThemeMenuOpen}
              aria-haspopup="menu"
            >
              {theme === 'dark' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            </button>
            {isThemeMenuOpen && (
              <div
                role="menu"
                className="absolute end-0 mt-2 w-36 rounded-lg bg-white dark:bg-ink-elevated border border-line dark:border-line-dark p-1 z-[60] text-xs shadow-sm"
              >
                {(
                  [
                    ['light', Sun, 'theme.light'],
                    ['dark', Moon, 'theme.dark'],
                    ['system', Laptop, 'theme.system'],
                  ] as [Theme, typeof Sun, string][]
                ).map(([value, Icon, labelKey]) => (
                  <button
                    key={value}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setTheme(value);
                      setIsThemeMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-start hover:bg-paper dark:hover:bg-ink-soft"
                  >
                    <Icon className="w-3.5 h-3.5 text-brand-500" /> {t(labelKey)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {isAuthenticated && (isAdminShell ? isAdmin : !isAdmin || experience === 'public') ? (
            <div className="hidden sm:flex items-center gap-1.5">
              {!isAdminShell && !isAdmin && (
                <>
                  <Link
                    to={accountPath}
                    className="text-[10px] font-semibold text-brand-700 dark:text-brand-300 max-w-[100px] truncate hover:underline"
                    title={user?.email}
                  >
                    {user?.name}
                  </Link>
                  <Link to={accountPath} className={iconBtn} aria-label={t('account.title')}>
                    <User className="w-4 h-4" />
                  </Link>
                  <Link to={portalPath} className={iconBtn} aria-label={t('nav.dashboard')}>
                    <Package className="w-4 h-4" />
                  </Link>
                </>
              )}
              {isAdminShell && (
                <>
                  <span className="text-[10px] font-semibold text-brand-700 dark:text-brand-300 max-w-[120px] truncate">
                    {user?.name}
                  </span>
                  <Link to="/admin/account" className={iconBtn} aria-label={t('account.title')}>
                    <User className="w-4 h-4" />
                  </Link>
                  <Link to="/admin" className={iconBtn} aria-label={t('nav.adminPortal')}>
                    <ShieldCheck className="w-4 h-4" />
                  </Link>
                </>
              )}
              <button
                type="button"
                onClick={() => void logout()}
                className={iconBtn}
                aria-label={t('portal.signOut')}
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            !isAdminShell && (
              <Link
                to={signInPath}
                className="hidden sm:flex bc-btn-primary !py-1.5 !px-3"
                aria-label={t('nav.signIn')}
              >
                <LogIn className="w-3.5 h-3.5" />
                <span className="hidden xl:inline">{t('nav.signIn')}</span>
              </Link>
            )
          )}

          {!isAdminShell && (
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden ${iconBtn}`}
              aria-label={t('nav.menu')}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          )}
        </div>
      </div>

      {isMobileMenuOpen && !isAdminShell && (
        <div className="lg:hidden border-b border-line dark:border-line-dark bg-paper dark:bg-ink p-4 space-y-1">
          {(
            [
              ...(desktopNav || []),
              ...(isAuthenticated && !isAdmin
                ? [
                    [portalPath, 'nav.dashboard'],
                    [accountPath, 'account.title'],
                  ]
                : [[signInPath, 'nav.signIn']]),
            ] as [string, string][]
          ).map(([path, key]) => (
            <Link
              key={`${path}-${key}`}
              to={path}
              onClick={() => setIsMobileMenuOpen(false)}
              className="block py-2.5 text-sm font-semibold text-ink dark:text-paper"
            >
              {t(key)}
            </Link>
          ))}
          {isAuthenticated && !isAdmin && (
            <button
              type="button"
              onClick={() => {
                setIsMobileMenuOpen(false);
                void logout();
              }}
              className="block w-full text-start py-2.5 text-sm font-semibold text-ink dark:text-paper"
            >
              {t('portal.signOut')}
            </button>
          )}
          <div className="pt-3">
            <PricingMarketSwitch />
          </div>
        </div>
      )}
    </header>
  );
};
